/**
 * Completion effects: a spark burst when a single alitehtävä is ticked off,
 * and a confetti fall when a whole päätehtävä lands on COMPLETED.
 *
 * Both are pure CSS — the keyframes `spark` and `confettiFall` live in
 * `index.css`. Nothing renders while idle; `useCelebration` mounts the pieces
 * on `fire()` and unmounts them when its timer runs out, so no animation is
 * left spinning in the background. Every animate class is `motion-safe:`, and
 * the pieces start at `opacity-0`, which means a viewer with reduced motion
 * enabled sees nothing at all rather than a frozen shape.
 *
 * Piece geometry comes from a seeded generator keyed on the run counter, so
 * each burst differs from the last while a re-render inside one burst keeps
 * the same layout.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { cn } from "@/lib/utils";

export const SPARK_MS = 2000;
export const CONFETTI_MS = 2900;

const SPARK_COUNT = 12;
const CONFETTI_COUNT = 28;

/**
 * Forest greens with a brass accent. The dots drop green-900: at 6px on a
 * white subtask row it reads as a dark speck rather than a colour, while a
 * 9x14px confetti piece has the area to carry it.
 */
const SPARK_COLORS = [
  "#166534",
  "#15803d",
  "#d97706",
  "#f0b429",
];

const CONFETTI_COLORS = [
  "#14532d",
  "#166534",
  "#15803d",
  "#d97706",
  "#f0b429",
];

/**
 * Run counter plus trigger. `0` means idle; every `fire()` bumps the counter
 * and restarts the teardown timer, so a rapid second completion replaces the
 * first burst instead of stacking on it.
 */
export function useCelebration(durationMs: number): [number, () => void] {
  const [runId, setRunId] = useState(0);
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const fire = useCallback(() => {
    setRunId((n) => n + 1);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setRunId(0), durationMs);
  }, [durationMs]);

  return [runId, fire];
}

/** Deterministic 0..1 generator, so one run's geometry is stable per render. */
function seededRandom(seed: number): () => number {
  let state = (seed * 9301 + 49297) % 233280;
  return () => {
    state = (state * 9301 + 49297) % 233280;
    return state / 233280;
  };
}

interface Spark {
  dx: number;
  dy: number;
  delayMs: number;
  size: number;
  color: string;
}

function buildSparks(seed: number): Spark[] {
  const rand = seededRandom(seed || 1);
  return Array.from({ length: SPARK_COUNT }, (_, i) => {
    const angle = (i / SPARK_COUNT) * Math.PI * 2 + rand() * 0.4;
    const distance = 26 + rand() * 16;
    return {
      dx: Math.cos(angle) * distance,
      dy: Math.sin(angle) * distance,
      delayMs: Math.round(rand() * 200),
      size: 5 + Math.round(rand() * 3),
      color: SPARK_COLORS[Math.floor(rand() * SPARK_COLORS.length)],
    };
  });
}

/**
 * Dots flying out of the centre of whatever wraps this. The wrapper needs
 * `relative`; the burst is sized to that box and overflows it well past its
 * edges on purpose, so nothing in the row may clip its overflow.
 */
export function SparkBurst({
  runId,
  className,
}: {
  runId: number;
  className?: string;
}) {
  const sparks = useMemo(() => buildSparks(runId), [runId]);
  if (runId === 0) return null;

  return (
    <span
      aria-hidden="true"
      className={cn("pointer-events-none absolute inset-0", className)}
    >
      {sparks.map((spark, i) => (
        <span
          key={i}
          className="absolute left-1/2 top-1/2 rounded-full opacity-0 motion-safe:animate-[spark_1.6s_cubic-bezier(0.25,0.8,0.35,1)_forwards]"
          style={
            {
              width: `${spark.size}px`,
              height: `${spark.size}px`,
              backgroundColor: spark.color,
              animationDelay: `${spark.delayMs}ms`,
              "--spark-dx": `${spark.dx.toFixed(1)}px`,
              "--spark-dy": `${spark.dy.toFixed(1)}px`,
            } as CSSProperties
          }
        />
      ))}
    </span>
  );
}

interface ConfettiPiece {
  left: number;
  dx: number;
  spin: number;
  width: number;
  height: number;
  color: string;
  delayMs: number;
  durationMs: number;
  round: boolean;
}

function buildConfetti(seed: number): ConfettiPiece[] {
  const rand = seededRandom(seed || 1);
  return Array.from({ length: CONFETTI_COUNT }, () => ({
    left: rand() * 100,
    dx: (rand() - 0.5) * 90,
    spin: 180 + rand() * 540 * (rand() > 0.5 ? 1 : -1),
    width: 5 + Math.round(rand() * 4),
    height: 8 + Math.round(rand() * 6),
    color: CONFETTI_COLORS[Math.floor(rand() * CONFETTI_COLORS.length)],
    delayMs: Math.round(rand() * 440),
    durationMs: 1800 + Math.round(rand() * 500),
    round: rand() > 0.75,
  }));
}

/**
 * Confetti falling across the nearest positioned ancestor. Clips itself, so
 * the host box needs nothing but `relative` (or `fixed`) and can keep its own
 * overflow rules.
 */
export function ConfettiBurst({
  runId,
  className,
}: {
  runId: number;
  className?: string;
}) {
  const pieces = useMemo(() => buildConfetti(runId), [runId]);
  if (runId === 0) return null;

  return (
    <span
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]",
        className,
      )}
    >
      {pieces.map((piece, i) => (
        <span
          key={i}
          className={cn(
            "absolute top-0 opacity-0 motion-safe:animate-[confettiFall_var(--confetti-duration)_ease-in_forwards]",
            piece.round ? "rounded-full" : "rounded-[1px]",
          )}
          style={
            {
              left: `${piece.left.toFixed(1)}%`,
              width: `${piece.width}px`,
              height: `${piece.round ? piece.width : piece.height}px`,
              backgroundColor: piece.color,
              animationDelay: `${piece.delayMs}ms`,
              "--confetti-duration": `${piece.durationMs}ms`,
              "--confetti-dx": `${piece.dx.toFixed(1)}px`,
              "--confetti-spin": `${piece.spin.toFixed(0)}deg`,
            } as CSSProperties
          }
        />
      ))}
    </span>
  );
}
